import axios from 'axios';
import type {
  DemandSummary,
  DemandDetail,
  DemandStatusCount,
  DemandBySystemCount,
  DemandDoneByMonth,
  DemandRepositoryEntry,
  DemandCostDetail,
  StackType,
  PageResponse,
} from '../types/engineering';

const BASE = `${import.meta.env.APP_BASE_API_URL}/api/demands`;

export const demandsApi = {
  getAll: (): Promise<DemandSummary[]> =>
    axios.get<DemandSummary[]>(BASE).then((r) => r.data),

  getById: (id: string): Promise<DemandDetail> =>
    axios.get<DemandDetail>(`${BASE}/${id}`).then((r) => r.data),

  update: (id: string, data: Record<string, unknown>): Promise<DemandDetail> =>
    axios.put<DemandDetail>(`${BASE}/${id}`, data).then((r) => r.data),

  // Home stats
  getStatusCounts: (): Promise<DemandStatusCount[]> =>
    axios.get<DemandStatusCount[]>(`${BASE}/stats/by-status`).then((r) => r.data),

  getOpenBySystem: (): Promise<DemandBySystemCount[]> =>
    axios.get<DemandBySystemCount[]>(`${BASE}/stats/open-by-system`).then((r) => r.data),

  getDoneByMonth: (): Promise<DemandDoneByMonth[]> =>
    axios.get<DemandDoneByMonth[]>(`${BASE}/stats/done-by-month`).then((r) => r.data),

  // Paginated list
  getPaginated: (params: {
    code?: string;
    status?: string;
    system?: string;
    title?: string;
    page?: number;
    size?: number;
  }): Promise<PageResponse<DemandSummary>> =>
    axios
      .get<PageResponse<DemandSummary>>(`${BASE}/paginated`, { params })
      .then((r) => r.data),

  // Sprint board
  getBySystem: (system: string): Promise<DemandSummary[]> =>
    axios
      .get<DemandSummary[]>(`${BASE}/by-system`, { params: { system } })
      .then((r) => r.data),

  // Distinct system names
  getSystems: (): Promise<string[]> =>
    axios.get<string[]>(`${BASE}/systems`).then((r) => r.data),

  // Repositories
  getRepositories: (demandId: string): Promise<DemandRepositoryEntry[]> =>
    axios.get<DemandRepositoryEntry[]>(`${BASE}/${demandId}/repositories`).then((r) => r.data),

  addRepository: (demandId: string, data: { repository: string; type?: StackType }): Promise<DemandRepositoryEntry> =>
    axios.post<DemandRepositoryEntry>(`${BASE}/${demandId}/repositories`, data).then((r) => r.data),

  deleteRepository: (demandId: string, repoId: number): Promise<void> =>
    axios.delete(`${BASE}/${demandId}/repositories/${repoId}`).then(() => undefined),

  // Send to Planning
  sendToPlanning: (demandId: string): Promise<DemandDetail> =>
    axios.post<DemandDetail>(`${BASE}/${demandId}/repositories/send-to-planning`).then((r) => r.data),

  // Retry Architect
  retryArchitect: (demandId: string): Promise<DemandDetail> =>
    axios.post<DemandDetail>(`${BASE}/${demandId}/repositories/retry-architect`).then((r) => r.data),

  // Cost details
  getCostDetails: (demandId: string): Promise<DemandCostDetail[]> =>
    axios.get<DemandCostDetail[]>(`${BASE}/${demandId}/cost-details`).then((r) => r.data),
};
